import { API_URL } from '@/config';
import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Vote, Upload, Save, AlertTriangle, RefreshCw, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${API_URL}/api`;

const AdminVoteRecoveryPage = () => {
  const { user } = useOutletContext();
  const [nominees, setNominees] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('all');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editedVotes, setEditedVotes] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadNominees();
  }, [selectedEvent]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const eventsRes = await axios.get(`${API}/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const pollingEvents = eventsRes.data.filter(e => e.polling_required);
      setEvents(pollingEvents);
    } catch (error) {
      console.error('Failed to load events:', error);
    }
  };

  const loadNominees = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const params = selectedEvent !== 'all' ? `?event_id=${selectedEvent}` : '';
      const response = await axios.get(`${API}/backup/nominees-for-vote-update${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNominees(response.data.nominees || []);
      setEditedVotes({});
    } catch (error) {
      toast.error('Failed to load nominees');
    } finally {
      setLoading(false);
    }
  };

  const handleVoteChange = (submissionId, value) => {
    const numValue = parseInt(value) || 0;
    setEditedVotes(prev => ({
      ...prev,
      [submissionId]: numValue
    }));
  };

  const handleSaveAll = async () => {
    const updates = Object.entries(editedVotes).map(([submissionId, votes]) => {
      const nominee = nominees.find(n => n.submission_id === submissionId);
      return {
        email: nominee?.user_email,
        name: nominee?.user_name,
        votes: votes
      };
    }).filter(u => u.email || u.name);

    if (updates.length === 0) {
      toast.error('No changes to save');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(`${API}/backup/update-vote-counts`, 
        { updates },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Updated ${response.data.updated} nominees`);
      if (response.data.not_found?.length > 0) {
        toast.warning(`${response.data.not_found.length} nominees not found`);
      }
      
      loadNominees();
    } catch (error) {
      toast.error('Failed to save vote counts');
    } finally {
      setSaving(false);
    }
  };

  const handleImportVoters = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      
      if (!parsed.collections?.voters_by_event) {
        toast.error('No voters_by_event found in backup file');
        return;
      }

      const token = localStorage.getItem('session_token');
      const response = await axios.post(`${API}/backup/import-voters`, 
        { voters_by_event: parsed.collections.voters_by_event },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to import voters');
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  const filteredNominees = nominees.filter(n => 
    !searchTerm || 
    n.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    n.user_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (user?.role !== 'Super Admin') {
    return (
      <div className="p-8 text-center">
        <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
        <h2 className="text-xl font-bold mb-2">Access Denied</h2>
        <p className="text-muted-foreground">Only Super Admins can access vote recovery.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-vote-recovery-page">
      <div>
        <h1 className="text-2xl font-bold" style={{ color: '#0F172A' }}>Vote Recovery</h1>
        <p className="text-sm text-muted-foreground">Manually update vote counts and import voter data</p>
      </div>

      <Alert className="border-amber-200 bg-amber-50">
        <AlertTriangle className="w-4 h-4 text-amber-600" />
        <AlertDescription className="text-amber-800">
          <strong>Data Recovery Mode:</strong> Use this page to restore vote counts from screenshots or records.
          Changes are saved directly to the database.
        </AlertDescription>
      </Alert>

      {/* Import Voters Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-blue-600" />
            Import Voter List from Backup
          </CardTitle>
          <CardDescription>
            Upload your backup JSON file to import the list of voters (who voted, not which nominee)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <input
            type="file"
            accept=".json"
            onChange={handleImportVoters}
            className="hidden"
            id="voter-import"
          />
          <Button
            variant="outline"
            onClick={() => document.getElementById('voter-import').click()}
            disabled={importing}
          >
            {importing ? (
              <span className="flex items-center gap-2">
                <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full"></div>
                Importing...
              </span>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Select Backup JSON to Import Voters
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Update Vote Counts Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Vote className="w-5 h-5 text-green-600" />
            Update Nominee Vote Counts
          </CardTitle>
          <CardDescription>
            Manually enter vote counts for each nominee based on your records
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Select value={selectedEvent} onValueChange={setSelectedEvent}>
              <SelectTrigger className="w-full sm:w-[300px]">
                <SelectValue placeholder="Filter by event" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Events</SelectItem>
                {events.map((event) => (
                  <SelectItem key={event.event_id} value={event.event_id}>
                    {event.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Button variant="outline" onClick={loadNominees}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Nominees Table */}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredNominees.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Vote className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No nominees found. Make sure form submissions are imported first.</p>
            </div>
          ) : (
            <>
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>Nominee Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead className="w-32">Current Votes</TableHead>
                      <TableHead className="w-32">New Votes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredNominees.map((nominee, index) => (
                      <TableRow key={nominee.submission_id}>
                        <TableCell className="font-medium">{index + 1}</TableCell>
                        <TableCell>{nominee.user_name || '-'}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {nominee.user_email || '-'}
                        </TableCell>
                        <TableCell>
                          <span className="font-semibold text-blue-600">
                            {nominee.vote_count || 0}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min="0"
                            placeholder={String(nominee.vote_count || 0)}
                            value={editedVotes[nominee.submission_id] ?? ''}
                            onChange={(e) => handleVoteChange(nominee.submission_id, e.target.value)}
                            className="w-24"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Save Button */}
              {Object.keys(editedVotes).length > 0 && (
                <div className="flex justify-end pt-4 border-t">
                  <Button 
                    onClick={handleSaveAll} 
                    disabled={saving}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {saving ? (
                      <span className="flex items-center gap-2">
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                        Saving...
                      </span>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save {Object.keys(editedVotes).length} Changes
                      </>
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminVoteRecoveryPage;
